import { AlertCircle, Mail, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { trpc } from '@/trpc/client'

function distanceFromNow(date: Date): string {
	const diff = date.getTime() - Date.now()
	const days = Math.round(diff / (1000 * 60 * 60 * 24))
	if (days < 0) return 'expired'
	if (days === 0) return 'expires today'
	if (days === 1) return 'expires in 1 day'
	return `expires in ${days} days`
}

export function MembersPage() {
	const currentQuery = trpc.organizations.getCurrent.useQuery()
	const membersQuery = trpc.organizations.listMembers.useQuery()
	const invitesQuery = trpc.organizations.listInvitations.useQuery()
	const utils = trpc.useUtils()

	const inviteMutation = trpc.organizations.invite.useMutation({
		onSuccess: async (result) => {
			await Promise.all([
				utils.organizations.listInvitations.invalidate(),
			])
			if (result.idempotent) {
				toast.info('That email already has a pending invitation.')
			} else {
				toast.success('Invitation sent.')
			}
		},
		onError: (err) => {
			toast.error(err.message || 'Could not send invitation.')
		},
	})

	const cancelInvitation = trpc.organizations.cancelInvitation.useMutation({
		onSuccess: async () => {
			await utils.organizations.listInvitations.invalidate()
			toast.success('Invitation canceled.')
		},
		onError: (err) => toast.error(err.message || 'Could not cancel.'),
	})

	const resendInvitation = trpc.organizations.resendInvitation.useMutation({
		onSuccess: async () => {
			await utils.organizations.listInvitations.invalidate()
			toast.success('Invitation resent.')
		},
		onError: (err) => toast.error(err.message || 'Could not resend.'),
	})

	const removeMember = trpc.organizations.removeMember.useMutation({
		onSuccess: async () => {
			await utils.organizations.listMembers.invalidate()
			toast.success('Member removed.')
		},
		onError: (err) => toast.error(err.message || 'Could not remove member.'),
	})

	const leave = trpc.organizations.leave.useMutation({
		onSuccess: async () => {
			await utils.organizations.list.invalidate()
			await utils.organizations.getCurrent.invalidate()
			await utils.invalidate()
			toast.success('You left the organization.')
		},
		onError: (err) => toast.error(err.message || 'Could not leave.'),
	})

	const [emailDraft, setEmailDraft] = useState('')

	async function handleInvite(e: React.FormEvent) {
		e.preventDefault()
		const trimmed = emailDraft.trim()
		if (!trimmed) return
		try {
			await inviteMutation.mutateAsync({ email: trimmed })
			setEmailDraft('')
		} catch {
			// surfaced by onError above
		}
	}

	const isOwner = currentQuery.data?.isOwner ?? false
	const pending = invitesQuery.data?.filter((i) => i.status === 'pending') ?? []

	return (
		<div className="space-y-8">
			{isOwner ? (
				<section className="rounded-xl border bg-card p-5 space-y-3">
					<div>
						<h2 className="text-sm font-semibold">Invite a member</h2>
						<p className="text-xs text-muted-foreground">
							They'll get an email with a link that expires in 7 days.
						</p>
					</div>
					<form onSubmit={handleInvite} className="flex gap-2">
						<div className="flex-1">
							<Label htmlFor="invite-email" className="sr-only">
								Email
							</Label>
							<Input
								id="invite-email"
								type="email"
								placeholder="teammate@example.com"
								value={emailDraft}
								onChange={(e) => setEmailDraft(e.target.value)}
								disabled={inviteMutation.isPending}
								maxLength={254}
								required
							/>
						</div>
						<Button
							type="submit"
							disabled={
								inviteMutation.isPending || emailDraft.trim().length === 0
							}
						>
							{inviteMutation.isPending ? 'Sending…' : 'Send invite'}
						</Button>
					</form>
				</section>
			) : null}

			<section className="space-y-3">
				<h2 className="text-sm font-semibold">Members</h2>
				{membersQuery.isPending ? (
					<Skeleton className="h-20 w-full" />
				) : membersQuery.isError ? (
					<Alert variant="destructive">
						<AlertCircle />
						<AlertTitle>Couldn't load members</AlertTitle>
						<AlertDescription>
							{membersQuery.error?.message ?? 'Unknown error'}
						</AlertDescription>
					</Alert>
				) : (
					<Card>
						<CardContent className="p-0">
							{membersQuery.data?.map((m) => (
								<div
									key={m.id}
									className="flex items-center justify-between gap-3 border-b px-4 py-3 last:border-b-0"
								>
									<div className="min-w-0">
										<div className="truncate text-sm font-medium">
											{m.user.name ?? m.user.email}
										</div>
										<div className="truncate text-xs text-muted-foreground">
											{m.user.email}
										</div>
									</div>
									<div className="flex items-center gap-2 shrink-0">
										<span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
											{m.role}
										</span>
										{isOwner && m.role !== 'owner' ? (
											<Button
												variant="ghost"
												size="sm"
												onClick={() => removeMember.mutate({ memberId: m.id })}
												disabled={removeMember.isPending}
												aria-label={`Remove ${m.user.email}`}
											>
												<Trash2 className="size-4" />
											</Button>
										) : null}
										{!isOwner &&
										m.user.email === currentQuery.data?.role ? null : null}
									</div>
								</div>
							))}
						</CardContent>
					</Card>
				)}
				{!isOwner ? (
					<div className="flex justify-end">
						<Button
							variant="outline"
							size="sm"
							onClick={() => leave.mutate()}
							disabled={leave.isPending}
						>
							{leave.isPending ? 'Leaving…' : 'Leave organization'}
						</Button>
					</div>
				) : null}
			</section>

			{isOwner ? (
				<section className="space-y-3">
					<h2 className="text-sm font-semibold">Pending invitations</h2>
					{invitesQuery.isPending ? (
						<Skeleton className="h-16 w-full" />
					) : invitesQuery.isError ? (
						<Alert variant="destructive">
							<AlertCircle />
							<AlertTitle>Couldn't load invitations</AlertTitle>
							<AlertDescription>
								{invitesQuery.error?.message ?? 'Unknown error'}
							</AlertDescription>
						</Alert>
					) : pending.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No pending invitations.
						</p>
					) : (
						<Card>
							<CardContent className="p-0">
								{pending.map((inv) => (
									<div
										key={inv.id}
										className="flex items-center justify-between gap-3 border-b px-4 py-3 last:border-b-0"
									>
										<div className="min-w-0 flex items-center gap-3">
											<Mail className="size-4 shrink-0 text-muted-foreground" />
											<div className="min-w-0">
												<div className="truncate text-sm font-medium">
													{inv.email}
												</div>
												<div className="truncate text-xs text-muted-foreground">
													{distanceFromNow(new Date(inv.expiresAt))} · invited
													by {inv.inviter.name ?? inv.inviter.email}
												</div>
											</div>
										</div>
										<div className="flex items-center gap-1 shrink-0">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													resendInvitation.mutate({ id: inv.id })
												}
												disabled={resendInvitation.isPending}
												aria-label="Resend invitation"
											>
												<RefreshCw className="size-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => cancelInvitation.mutate({ id: inv.id })}
												disabled={cancelInvitation.isPending}
												aria-label="Cancel invitation"
											>
												<Trash2 className="size-4" />
											</Button>
										</div>
									</div>
								))}
							</CardContent>
						</Card>
					)}
				</section>
			) : null}
		</div>
	)
}
